/**
 * Project / group / section CRUD + tracking + auto-verify + brief mutations
 * (RO-2-09b / #44415) — conversion of the 36 `"use server"` exports in
 * `excellent/src/projects/actions.ts`. None of these had a Next `route.ts`
 * twin; the Hono host is their first HTTP surface.
 *
 * Path design is body-only (no `:param`) for every mutation so none of this
 * bucket can interact with Hono's registration-order shadowing hazard (#44370).
 * The one GET (`listWorkspaceGraph` → `/api/projects/graph`) is a literal path
 * that coexists with the existing `/api/projects/graph/propose` + `/apply`
 * (project-admin.ts) without collision.
 */
import { z } from "zod";
import type { Operation } from "../types";

const okTrue = z.object({ ok: z.literal(true) });
type OkTrue = z.infer<typeof okTrue>;

// ── Projects ────────────────────────────────────────────────────────────────

const createBody = z.object({
  name: z.string(),
  description: z.string().optional(),
  groupId: z.number().int().positive().nullable().optional(),
});
export type CreateProjectBody = z.infer<typeof createBody>;
const createResult = z.object({
  ok: z.literal(true),
  slug: z.string(),
  redirectTo: z.string(),
});
export type CreateProjectResult = z.infer<typeof createResult>;

const createOp: Operation<void, void, CreateProjectBody, CreateProjectResult> = {
  method: "POST",
  path: () => "/api/projects/create",
  body: createBody,
  response: createResult,
};

const createForBoardBody = z.object({ name: z.string() });
const createForBoardResult = z.union([
  z.object({ ok: z.literal(true), slug: z.string() }),
  z.object({ ok: z.literal(false), reason: z.literal("name-required") }),
]);
const createForBoardOp: Operation<
  void,
  void,
  z.infer<typeof createForBoardBody>,
  z.infer<typeof createForBoardResult>
> = {
  method: "POST",
  path: () => "/api/projects/create-for-board",
  body: createForBoardBody,
  response: createForBoardResult,
};

const adoptClaudeBody = z.object({ claudeSlug: z.string() });
const adoptClaudeOp: Operation<void, void, z.infer<typeof adoptClaudeBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/adopt-claude",
  body: adoptClaudeBody,
  response: okTrue,
};

const slugBody = z.object({ slug: z.string().min(1) });
const archiveOp: Operation<void, void, z.infer<typeof slugBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/archive",
  body: slugBody,
  response: okTrue,
};
const restoreOp: Operation<void, void, z.infer<typeof slugBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/restore",
  body: slugBody,
  response: okTrue,
};

const renameBody = z.object({ slug: z.string().min(1), name: z.string() });
const renameOp: Operation<void, void, z.infer<typeof renameBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/rename",
  body: renameBody,
  response: okTrue,
};

const identityBody = z.object({
  slug: z.string().min(1),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
});
const identityOp: Operation<void, void, z.infer<typeof identityBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/identity",
  body: identityBody,
  response: okTrue,
};

const trackingResult = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
});
export type TrackingResultDTO = z.infer<typeof trackingResult>;

const trackingBody = z.object({
  slug: z.string().min(1),
  kind: z.string(),
  domains: z.array(z.string()),
});
const trackingOp: Operation<void, void, z.infer<typeof trackingBody>, TrackingResultDTO> = {
  method: "POST",
  path: () => "/api/projects/tracking",
  body: trackingBody,
  response: trackingResult,
};

const metricsBody = z.object({
  slug: z.string().min(1),
  metric: z.string(),
  value: z.number(),
  sampledOn: z.string(),
});
const metricsOp: Operation<void, void, z.infer<typeof metricsBody>, TrackingResultDTO> = {
  method: "POST",
  path: () => "/api/projects/metrics",
  body: metricsBody,
  response: trackingResult,
};

const trackedBody = z.object({
  name: z.string(),
  kind: z.string(),
  domains: z.array(z.string()),
  firstReading: z
    .object({
      metric: z.string(),
      value: z.number(),
      sampledOn: z.string(),
    })
    .nullable()
    .optional(),
});
const trackedResult = z.object({
  ok: z.boolean(),
  slug: z.string().optional(),
  error: z.string().optional(),
});
const trackedOp: Operation<void, void, z.infer<typeof trackedBody>, z.infer<typeof trackedResult>> = {
  method: "POST",
  path: () => "/api/projects/tracked",
  body: trackedBody,
  response: trackedResult,
};

const targetsBody = z.object({
  slug: z.string().min(1),
  targets: z.array(
    z.object({
      metric: z.string(),
      target: z.number().nullable(),
    }),
  ),
});
const targetsOp: Operation<void, void, z.infer<typeof targetsBody>, TrackingResultDTO> = {
  method: "POST",
  path: () => "/api/projects/targets",
  body: targetsBody,
  response: trackingResult,
};

const preferenceBody = z.object({
  slug: z.string().min(1),
  key: z.string(),
  value: z.unknown(),
});
const preferenceOp: Operation<void, void, z.infer<typeof preferenceBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/preference",
  body: preferenceBody,
  response: okTrue,
};

const preferenceDeleteBody = z.object({
  slug: z.string().min(1),
  key: z.string(),
});
const preferenceDeleteOp: Operation<void, void, z.infer<typeof preferenceDeleteBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/preference/delete",
  body: preferenceDeleteBody,
  response: okTrue,
};

const briefBody = z.object({
  slug: z.string().min(1),
  patch: z
    .object({
      purpose: z.string().optional(),
      audience: z.string().optional(),
      scope: z.string().optional(),
      growth: z.string().optional(),
      risks: z.string().optional(),
    })
    .partial(),
});
const briefOp: Operation<void, void, z.infer<typeof briefBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/brief",
  body: briefBody,
  response: okTrue,
};

const moveGroupBody = z.object({
  slug: z.string().min(1),
  groupId: z.number().int().positive().nullable(),
});
const moveGroupOp: Operation<void, void, z.infer<typeof moveGroupBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/move-group",
  body: moveGroupBody,
  response: okTrue,
};

const moveWorkspaceBody = z.object({
  slug: z.string().min(1),
  workspaceId: z.number().int().positive(),
});
const moveWorkspaceOp: Operation<void, void, z.infer<typeof moveWorkspaceBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/move-workspace",
  body: moveWorkspaceBody,
  response: okTrue,
};

const parentBody = z.object({
  slug: z.string().min(1),
  parentId: z.number().int().positive().nullable(),
});
const parentResult = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
});
const parentOp: Operation<void, void, z.infer<typeof parentBody>, z.infer<typeof parentResult>> = {
  method: "POST",
  path: () => "/api/projects/parent",
  body: parentBody,
  response: parentResult,
};

const coreOkResult = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
});
type CoreOkResult = z.infer<typeof coreOkResult>;

const roleBody = z.object({
  slug: z.string().min(1),
  role: z.string().nullable(),
});
const roleOp: Operation<void, void, z.infer<typeof roleBody>, CoreOkResult> = {
  method: "POST",
  path: () => "/api/projects/role",
  body: roleBody,
  response: coreOkResult,
};

const linkBody = z.object({
  fromSlug: z.string().min(1),
  toSlug: z.string().min(1),
  kind: z.string().min(1),
});
const linkOp: Operation<void, void, z.infer<typeof linkBody>, CoreOkResult> = {
  method: "POST",
  path: () => "/api/projects/link",
  body: linkBody,
  response: coreOkResult,
};

const unlinkBody = z.union([
  z.object({ id: z.number().int().positive() }),
  z.object({
    fromSlug: z.string().min(1),
    toSlug: z.string().min(1),
    kind: z.string().min(1),
  }),
]);
const unlinkOp: Operation<void, void, z.infer<typeof unlinkBody>, CoreOkResult> = {
  method: "POST",
  path: () => "/api/projects/unlink",
  body: unlinkBody,
  response: coreOkResult,
};

const unlinkRelationBody = z.object({ id: z.number().int().positive() });
const unlinkRelationOp: Operation<void, void, z.infer<typeof unlinkRelationBody>, CoreOkResult> = {
  method: "POST",
  path: () => "/api/projects/unlink-relation",
  body: unlinkRelationBody,
  response: coreOkResult,
};

const graphQuery = z.object({
  workspaceId: z.coerce.number().int().positive().optional(),
});
const graphResult = z.object({
  projects: z.array(z.unknown()),
  relationships: z.array(z.unknown()),
});
/** Full workspace graph (projects + relationships). Distinct from
 *  `projectAdmin.proposeGraph` (`/api/projects/graph/propose`). */
const graphOp: Operation<void, z.infer<typeof graphQuery>, void, z.infer<typeof graphResult>> = {
  method: "GET",
  path: () => "/api/projects/graph",
  query: graphQuery,
  response: graphResult,
};

// ── Groups ──────────────────────────────────────────────────────────────────

const createGroupBody = z.object({ name: z.string() });
const createGroupOp: Operation<void, void, z.infer<typeof createGroupBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/project-groups/create",
  body: createGroupBody,
  response: okTrue,
};

const renameGroupBody = z.object({
  groupId: z.number().int().positive(),
  name: z.string(),
});
const renameGroupOp: Operation<void, void, z.infer<typeof renameGroupBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/project-groups/rename",
  body: renameGroupBody,
  response: okTrue,
};

const archiveGroupBody = z.object({ groupId: z.number().int().positive() });
const archiveGroupOp: Operation<void, void, z.infer<typeof archiveGroupBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/project-groups/archive",
  body: archiveGroupBody,
  response: okTrue,
};

const reorderGroupBody = z.object({
  groupId: z.number().int().positive(),
  beforeGroupId: z.number().int().positive().nullable(),
  afterGroupId: z.number().int().positive().nullable(),
});
const reorderGroupOp: Operation<void, void, z.infer<typeof reorderGroupBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/project-groups/reorder",
  body: reorderGroupBody,
  response: okTrue,
};

// ── Sections ────────────────────────────────────────────────────────────────

const createSectionBody = z.object({
  projectSlug: z.string().min(1),
  name: z.string(),
});
const createSectionOp: Operation<void, void, z.infer<typeof createSectionBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/sections/create",
  body: createSectionBody,
  response: okTrue,
};

const renameSectionBody = z.object({
  projectSlug: z.string().min(1),
  sectionSlug: z.string().min(1),
  name: z.string(),
});
const renameSectionOp: Operation<void, void, z.infer<typeof renameSectionBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/projects/sections/rename",
  body: renameSectionBody,
  response: okTrue,
};

const archiveSectionBody = z.object({
  projectSlug: z.string().min(1),
  sectionSlug: z.string().min(1),
  cascadeTasks: z.boolean().optional(),
});
const archiveSectionResult = z.object({ archivedTasks: z.number().int().nonnegative() });
const archiveSectionOp: Operation<
  void,
  void,
  z.infer<typeof archiveSectionBody>,
  z.infer<typeof archiveSectionResult>
> = {
  method: "POST",
  path: () => "/api/projects/sections/archive",
  body: archiveSectionBody,
  response: archiveSectionResult,
};

const restoreSectionBody = z.object({
  projectSlug: z.string().min(1),
  sectionSlug: z.string().min(1),
  cascadeTasks: z.boolean().optional(),
});
const restoreSectionResult = z.object({ restoredTasks: z.number().int().nonnegative() });
const restoreSectionOp: Operation<
  void,
  void,
  z.infer<typeof restoreSectionBody>,
  z.infer<typeof restoreSectionResult>
> = {
  method: "POST",
  path: () => "/api/projects/sections/restore",
  body: restoreSectionBody,
  response: restoreSectionResult,
};

// ── Auto-verify ─────────────────────────────────────────────────────────────

const triggerBody = z.object({ taskId: z.number().int().positive() });
const triggerResult = z.object({
  ok: z.boolean(),
  runId: z.number().optional(),
  reason: z.string().optional(),
  message: z.string().optional(),
});
const triggerOp: Operation<void, void, z.infer<typeof triggerBody>, z.infer<typeof triggerResult>> = {
  method: "POST",
  path: () => "/projects/api/auto-verify/trigger",
  body: triggerBody,
  response: triggerResult,
};

const applyDraftBody = z.object({ runId: z.number().int().positive() });
const applyDraftResult = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  message: z.string(),
});
const applyDraftOp: Operation<void, void, z.infer<typeof applyDraftBody>, z.infer<typeof applyDraftResult>> = {
  method: "POST",
  path: () => "/projects/api/auto-verify/apply-draft",
  body: applyDraftBody,
  response: applyDraftResult,
};

const cancelBody = z.object({ runId: z.number().int().positive() });
const cancelResult = z.object({ ok: z.boolean(), message: z.string() });
const cancelOp: Operation<void, void, z.infer<typeof cancelBody>, z.infer<typeof cancelResult>> = {
  method: "POST",
  path: () => "/projects/api/auto-verify/cancel",
  body: cancelBody,
  response: cancelResult,
};

const autoVerifyConfigBody = z.object({
  enabled: z.boolean(),
  mode: z.enum(["draft", "autonomous"]),
  model: z.string(),
  max_turns: z.number(),
  max_runtime_seconds: z.number(),
  dangerous_skip_permissions: z.boolean(),
  allow_subtasks: z.boolean(),
  max_subtasks: z.number(),
});
const saveAutoVerifyOp: Operation<void, void, z.infer<typeof autoVerifyConfigBody>, OkTrue> = {
  method: "POST",
  path: () => "/projects/api/auto-verify/config",
  body: autoVerifyConfigBody,
  response: okTrue,
};
/** Form-shaped twin of saveAutoVerifyConfig (same fields; JSON, not FormData). */
const saveAutoVerifyFormOp: Operation<void, void, z.infer<typeof autoVerifyConfigBody>, OkTrue> = {
  method: "POST",
  path: () => "/projects/api/auto-verify/config/form",
  body: autoVerifyConfigBody,
  response: okTrue,
};

// ── Section audit config ────────────────────────────────────────────────────

const sectionAuditBody = z.object({
  enabled: z.boolean(),
  model: z.string(),
  cli: z.enum(["claude", "codex"]),
  max_runtime_seconds: z.number(),
  default_cadence_days: z.number(),
  dangerous_skip_permissions: z.boolean(),
});
const sectionAuditOp: Operation<void, void, z.infer<typeof sectionAuditBody>, OkTrue> = {
  method: "POST",
  path: () => "/api/settings/section-audits",
  body: sectionAuditBody,
  response: okTrue,
};

export const projectActions = {
  create: createOp,
  createForBoard: createForBoardOp,
  adoptClaude: adoptClaudeOp,
  archive: archiveOp,
  restore: restoreOp,
  rename: renameOp,
  identity: identityOp,
  tracking: trackingOp,
  metrics: metricsOp,
  tracked: trackedOp,
  targets: targetsOp,
  preference: preferenceOp,
  preferenceDelete: preferenceDeleteOp,
  brief: briefOp,
  moveGroup: moveGroupOp,
  moveWorkspace: moveWorkspaceOp,
  parent: parentOp,
  role: roleOp,
  link: linkOp,
  unlink: unlinkOp,
  unlinkRelation: unlinkRelationOp,
  graph: graphOp,
  createGroup: createGroupOp,
  renameGroup: renameGroupOp,
  archiveGroup: archiveGroupOp,
  reorderGroup: reorderGroupOp,
  createSection: createSectionOp,
  renameSection: renameSectionOp,
  archiveSection: archiveSectionOp,
  restoreSection: restoreSectionOp,
  triggerAutoVerify: triggerOp,
  applyDraft: applyDraftOp,
  cancelRun: cancelOp,
  saveAutoVerifyConfig: saveAutoVerifyOp,
  saveAutoVerifyConfigForm: saveAutoVerifyFormOp,
  saveSectionAuditConfig: sectionAuditOp,
};
